<template>
<div class="page-container" :style="backgroundStyle">
    <!-- 动态粒子背景 -->
    <div class="particles">
        <div v-for="n in 30" :key="n" class="particle" :style="getParticleStyle()"></div>
    </div>
    
    <!-- 渐变遮罩层 -->
    <div class="gradient-overlay"></div>
    
    <!-- 主内容区域 -->
    <div class="content-wrapper">
        <!-- 返回按钮 -->
        <router-link to="/" class="back-button">
            <LucideIcon name="arrow-left" :size="18" />
            <span>返回首页</span>
        </router-link>
        
        <div class="glass-card" :class="{ 'show': isLoaded }">
            <!-- 页面标题 -->
            <div class="page-header">
                <div class="icon-wrapper icon-blue">
                    <LucideIcon name="link" :size="28" color="#fff" />
                </div>
                <h1 class="title">订阅链接</h1>
                <p class="subtitle" v-if="currentUser">{{ currentUser.name }} 的专属订阅</p>
                <p class="subtitle" v-else>点击链接自动复制到剪贴板</p>
            </div>
            
            <!-- 分隔线 -->
            <div class="divider">
                <div class="divider-line"></div>
                <div class="divider-dot"></div>
                <div class="divider-line"></div>
            </div>
            
            <!-- 有 token 参数时：显示该用户的专属链接 -->
            <div v-if="token && currentUser" class="links-container" :class="{ 'show': showSections }">
                <div class="links-header">
                    <LucideIcon name="zap" :size="20" color="var(--color-primary)" />
                    <h2>高速地区 · Clash YAML</h2>
                    <span class="section-badge">全平台通用</span>
                </div>
                <div class="link-items">
                    <a
                        href="javascript:void(0)"
                        @click.prevent="copySubscribeLink()"
                        class="link-item"
                    >
                        <LucideIcon name="zap" :size="18" color="var(--color-accent)" />
                        <span class="link-text">Clash</span>
                        <span class="link-desc">已过滤信息节点 · 含分流规则</span>
                        <span class="link-action">
                            <LucideIcon name="copy" :size="14" />
                            复制
                        </span>
                    </a>
                </div>
            </div>

            <!-- 无 token 参数时：引导用户 -->
            <div v-else-if="!token" class="no-token-notice">
                <div class="notice-icon-wrap">
                    <LucideIcon name="lock" :size="48" color="var(--color-muted)" />
                </div>
                <p class="notice-title">需要专属订阅链接</p>
                <p class="notice-desc">请前往用户管理面板获取您的专属订阅链接</p>
                <router-link to="/admin" class="go-admin-btn">
                    <LucideIcon name="users" :size="16" />
                    前往用户管理
                </router-link>
            </div>

            <!-- token 无效时 -->
            <div v-else-if="token && !currentUser && !loading" class="no-token-notice">
                <div class="notice-icon-wrap">
                    <LucideIcon name="x-circle" :size="48" color="var(--color-danger-light)" />
                </div>
                <p class="notice-title">链接无效</p>
                <p class="notice-desc">该订阅 Token 不存在或已被禁用</p>
                <router-link to="/admin" class="go-admin-btn">
                    <LucideIcon name="users" :size="16" />
                    前往用户管理
                </router-link>
            </div>
            
            <!-- 底部提示 -->
            <div class="footer-tip" v-if="currentUser">
                <LucideIcon name="lightbulb" :size="16" color="rgba(255, 193, 7, 0.9)" />
                <span>复制后请粘贴到客户端订阅地址中</span>
            </div>
        </div>
    </div>
    
    <!-- Toast 提示 -->
    <transition name="toast">
        <div v-if="showToast" class="toast" :class="toastType">
            <LucideIcon :name="toastType === 'success' ? 'check-circle' : 'x-circle'" :size="18" />
            <span>{{ toastMessage }}</span>
        </div>
    </transition>
</div>
</template>

<script>
import backgroundMixin from '@/mixins/background'

export default {
    name: 'SubscribeLinks',
    mixins: [backgroundMixin],
    data() {
        return {
            showSections: false,
            showToast: false,
            toastMessage: '',
            toastType: 'success',
            token: '',
            currentUser: null,
            currentUserIndex: 0,
            loading: true
        }
    },
    methods: {
        // 根据 token 查找用户
        async findUserByToken() {
            if (!this.token) {
                this.loading = false;
                return;
            }
            try {
                const res = await this.$axios.get('/api/users');
                const users = res.data;
                const userIndex = users.findIndex(u => u.token === this.token && u.enabled);
                this.currentUserIndex = userIndex === -1 ? 0 : userIndex + 1;
                this.currentUser = userIndex === -1 ? null : users[userIndex];
            } catch (err) {
                console.error('查询用户失败:', err);
            } finally {
                this.loading = false;
            }
        },
        getClashProfileName() {
            return `Airport-${String(this.currentUserIndex).padStart(3, '0')}.yaml`;
        },
        // 复制订阅链接（统一输出 Clash YAML，各客户端通用）
        async copySubscribeLink() {
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/api/subscribe/${this.token}/${this.getClashProfileName()}?target=clash`;
            try {
                await navigator.clipboard.writeText(url);
                this.showToastMessage('订阅链接已复制！', 'success');
            } catch (err) {
                console.error('无法复制链接: ', err);
                this.showToastMessage('复制失败，请手动复制', 'error');
            }
        },
        showToastMessage(message, type = 'success') {
            this.toastMessage = message;
            this.toastType = type;
            this.showToast = true;
            
            setTimeout(() => {
                this.showToast = false;
            }, 2500);
        }
    },
    mounted() {
        // Mixin 已处理 fetchBackground() 和 triggerLoadAnimation()

        // 从 URL query 中取 token
        this.token = this.$route.query.token || '';
        this.findUserByToken();

        setTimeout(() => {
            this.showSections = true;
        }, 400);
    }
}

</script>

<style scoped>
/* ===== 基础容器 ===== */
.page-container {
    position: relative;
    min-height: 100vh;
    width: 100vw;
    overflow-x: hidden;
    font-family: var(--font-body);
    color: var(--color-ink);
}

/* ===== 粒子动画 ===== */
.particles {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    overflow: hidden;
    z-index: 1;
    pointer-events: none;
}

.particle {
    position: absolute;
    bottom: -20px;
    background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%);
    border-radius: 50%;
    animation: float-up linear infinite;
}

@keyframes float-up {
    0% { transform: translateY(0); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(-100vh); opacity: 0; }
}

/* ===== 渐变遮罩 ===== */
.gradient-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg, rgba(26, 26, 46, 0.7) 0%, rgba(22, 33, 62, 0.6) 100%);
    z-index: 2;
    pointer-events: none;
}

/* ===== 内容包装器 ===== */
.content-wrapper {
    position: relative;
    z-index: 10;
    padding: var(--space-xl);
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* ===== 返回按钮 ===== */
.back-button {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 10px var(--space-xl);
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-md);
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-size: 0.95rem;
    margin-bottom: var(--space-xl);
    transition: all var(--duration-normal) ease;
    cursor: pointer;
}

.back-button:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-5px);
    color: #fff;
}

/* ===== 玻璃态卡片 ===== */
.glass-card {
    width: 100%;
    max-width: 600px;
    background: var(--color-surface-glass);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: var(--radius-2xl);
    border: 1px solid var(--color-border);
    padding: var(--space-4xl) var(--space-3xl);
    box-shadow: var(--shadow-card);
    transform: translateY(30px) scale(0.95);
    opacity: 0;
    transition: all var(--duration-slow) var(--ease-out);
}

.glass-card.show {
    transform: translateY(0) scale(1);
    opacity: 1;
}

/* ===== 页面头部 ===== */
.page-header {
    text-align: center;
    margin-bottom: var(--space-2xl);
}

.icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: var(--radius-xl);
    margin-bottom: var(--space-lg);
}

.icon-blue {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    box-shadow: 0 4px 20px rgba(79, 172, 254, 0.4);
}

.title {
    font-size: 2rem;
    font-weight: 700;
    font-family: var(--font-display);
    background: linear-gradient(90deg, #4facfe, #00f2fe, #f093fb, #4facfe);
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradient-flow 4s ease infinite;
    margin-bottom: var(--space-sm);
}

@keyframes gradient-flow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
}

.subtitle {
    color: var(--color-subtle);
    font-size: 0.95rem;
}

/* ===== 分隔线 ===== */
.divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    margin: var(--space-2xl) 0;
}

.divider-line {
    height: 1px;
    width: 60px;
    background: linear-gradient(90deg, transparent, rgba(79, 172, 254, 0.5), transparent);
}

.divider-dot {
    width: 6px;
    height: 6px;
    background: linear-gradient(135deg, #4facfe, #00f2fe);
    border-radius: 50%;
}

/* ===== 链接区块 ===== */
.links-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.5s ease;
}

.links-container.show {
    opacity: 1;
    transform: translateY(0);
}

.links-header {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.links-header h2 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin: 0;
}

.section-badge {
    font-size: 0.75rem;
    padding: 4px 10px;
    background: rgba(79, 172, 254, 0.2);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: var(--radius-pill);
    color: #4facfe;
}

/* ===== 链接项 ===== */
.link-items {
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
}

.link-item {
    display: flex;
    align-items: center;
    gap: var(--space-md);
    padding: 14px 18px;
    background: var(--color-surface-elevated);
    border: 1px solid var(--color-border-subtle);
    border-radius: 14px;
    color: #fff;
    text-decoration: none;
    transition: all var(--duration-normal) ease;
    cursor: pointer;
}

.link-item:hover {
    background: rgba(79, 172, 254, 0.15);
    border-color: rgba(79, 172, 254, 0.4);
    transform: translateX(5px);
    box-shadow: var(--shadow-hover);
}

.link-text {
    font-size: 1rem;
    font-weight: 500;
}

.link-desc {
    font-size: 0.8rem;
    color: var(--color-subtle);
}

.link-action {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    color: var(--color-subtle);
    transition: color var(--duration-normal) ease;
}

.link-item:hover .link-action {
    color: #4facfe;
}

/* ===== 底部提示 ===== */
.footer-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-sm);
    margin-top: 28px;
    padding: var(--space-md);
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.2);
    border-radius: var(--radius-md);
    color: rgba(255, 193, 7, 0.9);
    font-size: 0.9rem;
}

/* ===== Toast 提示 ===== */
.toast {
    position: fixed;
    top: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px var(--space-2xl);
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(20px);
    border-radius: 14px;
    color: #fff;
    font-size: 1rem;
    z-index: 1000;
    box-shadow: var(--shadow-card);
}

.toast.success {
    border: 1px solid rgba(76, 175, 80, 0.5);
}

.toast.error {
    border: 1px solid rgba(244, 67, 54, 0.5);
}

.toast-enter-active, .toast-leave-active {
    transition: all 0.4s ease;
}

.toast-enter, .toast-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
}

/* ===== 无 Token 提示 ===== */
.no-token-notice {
    text-align: center;
    padding: var(--space-4xl) var(--space-xl);
}

.notice-icon-wrap {
    margin-bottom: var(--space-lg);
}

.notice-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: var(--space-sm);
}

.notice-desc {
    font-size: 0.95rem;
    color: var(--color-subtle);
    margin-bottom: var(--space-2xl);
}

.go-admin-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-md) 28px;
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.25), rgba(58, 183, 255, 0.25));
    border: 1px solid rgba(110, 231, 255, 0.4);
    border-radius: 14px;
    color: #fff;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 600;
    transition: all var(--duration-normal) ease;
    cursor: pointer;
}

.go-admin-btn:hover {
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.4), rgba(58, 183, 255, 0.4));
    transform: translateY(-2px);
    box-shadow: var(--shadow-glow-primary);
}

/* ===== 响应式 ===== */
@media (max-width: 480px) {
    .glass-card {
        padding: 28px var(--space-xl);
        border-radius: var(--radius-xl);
    }
    
    .title {
        font-size: 1.6rem;
    }
    
    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }
    
    .link-item {
        padding: var(--space-md) 14px;
    }
}

@media (min-width: 481px) and (max-width: 768px) {
    .glass-card {
        max-width: 520px;
    }
}

@media (min-width: 1024px) {
    .glass-card {
        max-width: 640px;
        padding: 48px 40px;
    }
}
</style>
